// src/pages/ShopPage/ProductCarousel/ProductCarousel.tsx

import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { API_BASE_URL } from '../../../config';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import styles from './ProductCarousel.module.css';

const useIsMobile = (breakpoint = 999) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
};

interface ShopProduct {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  imageUrl: string;
  externalUrl: string;
}

const ProductCarousel: React.FC = () => {
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeItem, setActiveItem] = useState('');
  const [loading, setLoading] = useState(true);
  const [menuSwiper, setMenuSwiper] = useState<SwiperType | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/products/shop`);
        if (!res.ok) throw new Error('Falha ao buscar produtos');
        const data: ShopProduct[] = await res.json();
        setProducts(data);
        const cats = Array.from(new Set(data.map(p => p.category)));
        setCategories(cats);
        if (cats.length > 0) setActiveItem(cats[0]);
      } catch {
        setProducts([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const productsToDisplay = products.filter(p => p.category === activeItem);

  const handleMenuItemClick = (item: string, index: number) => {
    setActiveItem(item);
    if (isMobile) {
      menuSwiper?.slideTo(index);
    }
  };

  if (loading) {
    return (
      <div className={styles.pageContentWrapper}>
        <div className={styles.noProductsMessage}><p>Carregando produtos...</p></div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className={styles.pageContentWrapper}>
        <div className={styles.noProductsMessage}><p>Nenhum produto disponível no momento.</p></div>
      </div>
    );
  }

  return (
    <div className={styles.pageContentWrapper}>
      <aside className={styles.sideNavColumn}>
        <nav className={styles.navContainer}>
          {isMobile ? (
            <Swiper
              className={styles.menuSwiperContainer}
              onSwiper={setMenuSwiper}
              slidesPerView={'auto'}
              centeredSlides={true}
              spaceBetween={10}
              slideToClickedSlide={true}
              onSlideChange={(swiper: SwiperType) => setActiveItem(categories[swiper.realIndex])}
            >
              {categories.map((item, index) => (
                <SwiperSlide key={item} className={styles.menuSwiperSlide}>
                  <button
                    className={`${styles.navButton} ${activeItem === item ? styles.active : ''}`}
                    onClick={() => handleMenuItemClick(item, index)}
                  >
                    {item}
                  </button>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <ul className={styles.navList}>
              {categories.map((item, index) => (
                <li key={item}>
                  <button
                    className={`${styles.navButton} ${activeItem === item ? styles.active : ''}`}
                    onClick={() => handleMenuItemClick(item, index)}
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </nav>
      </aside>

      <div className={styles.carouselColumn}>
        <div className={styles.carouselWrapper}>
          {productsToDisplay.length > 0 ? (
            <Swiper
              key={activeItem}
              modules={[Pagination, Navigation]}
              navigation={{ nextEl: '.swiper-button-next-custom', prevEl: '.swiper-button-prev-custom' }}
              spaceBetween={30}
              loop={false}
              pagination={{ clickable: true }}
              className={styles.swiperContainer}
              breakpoints={{
                320: { slidesPerView: 1, spaceBetween: 20 },
                768: { slidesPerView: 2, spaceBetween: 30 },
                1024: { slidesPerView: 3, spaceBetween: 30 }
              }}
            >
              {productsToDisplay.map((product) => (
                <SwiperSlide key={product.id} className={styles.swiperSlide}>
                  <div className={styles.productCard}>
                    <div className={styles.productImageWrapper}>
                      <img src={product.imageUrl} alt={product.name} />
                    </div>
                    <div className={styles.productInfo}>
                      <h3>{product.name}</h3>
                      {product.price > 0 && (
                        <span className={styles.productPrice}>R$ {product.price.toFixed(2)}</span>
                      )}
                      <div className={styles.actions}>
                        <button className={styles.iconButton} aria-label="Adicionar ao carrinho">
                          <span className="material-symbols-outlined notranslate" translate="no">add_shopping_cart</span>
                        </button>
                        {product.externalUrl && (
                          <a href={product.externalUrl} target="_blank" rel="noopener noreferrer" className={styles.iconButton} aria-label="Ver detalhes">
                            <span className="material-symbols-outlined notranslate" translate="no">visibility</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className={styles.noProductsMessage}>
              <p>Nenhum produto encontrado nesta categoria.</p>
            </div>
          )}

          <div className="swiper-button-prev-custom">
            <span className="material-symbols-outlined notranslate" translate="no">keyboard_double_arrow_left</span>
          </div>
          <div className="swiper-button-next-custom">
            <span className="material-symbols-outlined notranslate" translate="no">keyboard_double_arrow_right</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCarousel;